import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useLocale, useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import { getCurrentOperatorActorId } from '../../utils/operatorSession'
import { inventoryApi } from '../../utils/inventoryApi'
import {
  feedLogApi,
  type FeedLogMutationResult
} from '../../utils/feedLogApi'
import type { InventoryItem } from '../../types/inventory'
import { createId } from '../../utils/id'
import { latestReceiptLotNumber, readMix, splitMixKg, validateMix } from '../../utils/feedMix'
import { feedPlanMode, feedPlanTotalKg, plannedKgFor, readFeedPlan, resolvePlanFeedItems, withFeedPlanTotalKg } from '../../utils/unitPlans'
import { checkFeedStop, latestReadingsOfDay, readFeedRules } from '../../utils/feedRules'
import {
  backToNormalMetadata,
  crewTransferState,
  daysBetween,
  isBackToNormal,
  readPlannedTransfer,
  readTransferRation
} from '../../utils/transferProtocol'
import { loadWaterTableRows, refreshWaterTableRowsFromApi } from '../../utils/waterTable'
import { pickReading, resolveMeasuringPoint } from '../../utils/measuringPoint'
import { loadJSON, saveJSON } from '../../utils/storage'
import { useLots } from '../../hooks/useLots'
import FeedHoldPanel from './FeedHoldPanel'
import { fedFromWeighing, parseLeftover, readDayWeighing, withDayWeighing } from '../../utils/dayWeighing'
import type { FeedLog } from '../../types/production'
import OperatorGate from './OperatorGate'
import CrewModeFarmBar from './CrewModeFarmBar'
import { useCrewModeFarm } from './useCrewModeFarm'
import { isFeedCrewModeEnabled } from './feedLogFeatureFlag'
import { recordCrewEvent } from './crewModeTelemetry'
import {
  buildFeedCaptureCreatePayload,
  buildMixFeedCapturePayloads,
  findLikelyDuplicateFeedLog,
  isUnusuallyLargeFeedKg,
  validateFeedKgValue
} from './feedCaptureValidation'
import { useCrewLocation } from './useCrewLocation'
import { CrewLocationHeader, CrewLocationPicker, CrewNextLocationButton } from './CrewLocationPicker'
import { buildOperatorUndoAudit, lastFeedTypeForTank } from './crewCaptureHelpers'
import { ensureTodayProductionRow } from './ensureTodayProductionRow'
import { farmDayKey, farmTimeZone } from '../../utils/farmTime'
import { isUnsyncedBadgeState, useRecheckWhileUnsynced } from './useRecheckWhileUnsynced'

// Feed v0 PRD — Stage 4. Charter U-2 interaction budget: 4 taps warm (pond
// chip, prefilled -> item chip -> quantity entry -> confirm) when the pond
// has a single active lot; 5 taps cold / multi-lot-pond (+ lot sub-select).
// Charter O-5: Confirm never waits on network — writes locally via the
// existing feedLogApi/Dexie/outbox path. Charter D-3: the pond -> lot step
// resolves both productionUnitId and lotId before quantity entry; a pond
// with no active lot blocks the flow rather than guessing. Charter D-4:
// capturedByActorId is stamped from the active OperatorGate session.
// Charter O-3: this screen never invokes client-side cost derivation — the
// `deriveCostServerSide` flag tells the server to derive cost from
// authoritative inventory pricing once the entry syncs (FeedLogService).

type SyncBadgeState = 'local' | 'pending' | 'synced' | 'error'

const chipButtonStyle: React.CSSProperties = {
  minHeight: 56,
  borderRadius: 12,
  border: '1px solid #d1d5db',
  backgroundColor: 'white',
  fontWeight: 800,
  fontSize: 14,
  cursor: 'pointer',
  padding: '8px 12px',
  width: '100%',
  textAlign: 'left'
}

const primaryButtonStyle: React.CSSProperties = {
  minHeight: 56,
  borderRadius: 12,
  border: '1px solid #1d4ed8',
  backgroundColor: '#1d4ed8',
  color: 'white',
  fontWeight: 800,
  fontSize: 16,
  cursor: 'pointer',
  width: '100%'
}

const syncBadgeStyle: Record<SyncBadgeState, React.CSSProperties> = {
  local: { backgroundColor: '#f3f4f6', color: '#374151' },
  pending: { backgroundColor: '#fffbeb', color: '#92400e' },
  synced: { backgroundColor: '#f0fdf4', color: '#166534' },
  error: { backgroundColor: '#fef2f2', color: '#991b1b' }
}

const syncBadgeLabelKey: Record<SyncBadgeState, TranslationKey> = {
  local: 'crewMode.feed.sync.local',
  pending: 'crewMode.feed.sync.pending',
  synced: 'crewMode.feed.sync.synced',
  error: 'crewMode.feed.sync.error'
}

const toSyncBadgeState = (syncState: FeedLogMutationResult['syncState']): SyncBadgeState => (
  syncState === 'synced' ? 'synced' : syncState === 'pending' ? 'pending' : 'local'
)

type LastCaptureStatus = {
  feedLogId: string
  // Office Plans v0 §3.2: a mix feeding writes one record per component;
  // undo and retry act on all of them (C3). Single feeds: [feedLogId].
  feedLogIds: string[]
  itemName: string
  feedKgLabel: string
  locationLabel: string
  state: SyncBadgeState
}

const feedTypeForItem = (item: InventoryItem): string => item.usageFeedType?.trim() || item.name

const formatKg = (value: number, locale: string): string =>
  value.toLocaleString(locale, { maximumFractionDigits: 3 })

export default function FeedCapture() {
  const t = useT()
  const locale = useLocale()
  const { farms, farmId, selectFarm } = useCrewModeFarm()
  const navigate = useNavigate()
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([])
  const [selectedItemId, setSelectedItemId] = useState('')
  const [feedKgDraft, setFeedKgDraft] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [largeValuePending, setLargeValuePending] = useState(false)
  const [duplicateWarning, setDuplicateWarning] = useState<FeedLog | null>(null)
  const [existingLogsForLot, setExistingLogsForLot] = useState<FeedLog[]>([])
  const [lastCapture, setLastCapture] = useState<LastCaptureStatus | null>(null)
  const feedKgInputRef = useRef<HTMLInputElement>(null)

  // Field Bundle v0 §3 items 1–4: one shared location ("Lot · Unidad").
  const crewLocation = useCrewLocation({ stream: 'feed', farmId, farms })
  const { location } = crewLocation
  const productionUnitId = location?.productionUnitId ?? ''
  const selectedLotId = location?.lotId ?? ''

  useEffect(() => {
    if (!farmId) return
    recordCrewEvent('capture.start', { stream: 'feed', farmId })
  }, [farmId])

  useEffect(() => {
    let cancelled = false
    void inventoryApi.listAll().then((items) => {
      if (cancelled) return
      setInventoryItems(items)
    })
    return () => {
      cancelled = true
    }
  }, [farmId])

  const farmFeedItems = useMemo(
    () => inventoryItems.filter((item) => item.farmId === farmId && item.category === 'feed'),
    [inventoryItems, farmId]
  )
  // Office Plans v0 §3.2 / C2: mixes are offered like any feed, but only when
  // the recipe is valid (2+ existing feeds, percentages adding up to 100).
  const feedItems = useMemo(
    () => farmFeedItems.filter((item) => {
      const mix = readMix(item)
      return !mix || validateMix(mix, farmFeedItems).ok
    }),
    [farmFeedItems]
  )
  const feedItemsRef = useRef<InventoryItem[]>([])
  feedItemsRef.current = feedItems

  // Office Plans v0 §3.1: the tank's feed plan (set in Editar instalación).
  // A plan whose feed was deleted or whose mix became invalid is ignored (C1).
  const currentUnit = useMemo(
    () => farms.find((farm) => farm.id === farmId)?.productionUnits?.find((unit) => unit.id === productionUnitId) ?? null,
    [farms, farmId, productionUnitId]
  )
  const officePlan = useMemo(() => readFeedPlan(currentUnit), [currentUnit])

  // Findings #210: a planned move (fasting days) or the reduced ration after a move,
  // read from the lot the office set; "Ya comen normal" ends the reduced days early.
  const { lots } = useLots({ farmId, enabled: Boolean(farmId) })
  const currentLot = useMemo(() => lots.find((lot) => lot.id === selectedLotId) ?? null, [lots, selectedLotId])
  const timeZone = farmTimeZone(farmId)
  const today = farmDayKey(new Date().toISOString(), timeZone) ?? ''
  const transferRation = useMemo(() => readTransferRation(currentLot), [currentLot])
  const backToNormalKey = transferRation ? `crewFeed.backToNormal::${selectedLotId}::${transferRation.movedOn}` : ''
  const [backToNormalTapped, setBackToNormalTapped] = useState(false)
  useEffect(() => {
    setBackToNormalTapped(backToNormalKey ? loadJSON<boolean>(backToNormalKey, false) === true : false)
  }, [backToNormalKey])
  const transferState = crewTransferState({
    planned: readPlannedTransfer(currentLot),
    ration: transferRation,
    productionUnitId,
    today,
    backToNormal: backToNormalTapped || Boolean(transferRation && isBackToNormal(existingLogsForLot, selectedLotId, transferRation.movedOn, timeZone))
  })
  const reducedState = transferState?.kind === 'reduced' ? transferState : null
  // The reduced days give the reduced kg/día, each feed keeping its share of the plan.
  const feedPlan = useMemo(
    () => (officePlan && reducedState?.kgPerDay ? withFeedPlanTotalKg(officePlan, reducedState.kgPerDay) : officePlan),
    [officePlan, reducedState?.kgPerDay]
  )

  // Findings #211: the office's stop rules against today's last readings of this unit.
  // Findings #217: or of its measuring point when the unit has none today. Readings
  // taken on another phone come from the server (best effort; offline keeps this phone's).
  const feedRules = useMemo(() => readFeedRules(currentUnit), [currentUnit])
  const measuringPoint = useMemo(
    () => resolveMeasuringPoint(currentUnit, farms.find((farm) => farm.id === farmId)?.productionUnits ?? []),
    [currentUnit, farms, farmId]
  )
  const measuringPointId = measuringPoint?.id ?? null
  const [waterRows, setWaterRows] = useState<ReturnType<typeof loadWaterTableRows>>([])
  const [pointWaterRows, setPointWaterRows] = useState<ReturnType<typeof loadWaterTableRows>>([])
  const hasFeedRules = Boolean(feedRules)
  useEffect(() => {
    if (!farmId || !productionUnitId || !hasFeedRules) {
      setWaterRows([])
      setPointWaterRows([])
      return
    }
    let cancelled = false
    const load = () => {
      if (cancelled) return
      setWaterRows(loadWaterTableRows({ farmId, productionUnitId }))
      setPointWaterRows(measuringPointId ? loadWaterTableRows({ farmId, productionUnitId: measuringPointId }) : [])
    }
    load()
    const unitIds = measuringPointId ? [productionUnitId, measuringPointId] : [productionUnitId]
    void Promise.allSettled(unitIds.map((unitId) => refreshWaterTableRowsFromApi({ farmId, productionUnitId: unitId }))).then(load)
    return () => {
      cancelled = true
    }
  }, [farmId, productionUnitId, measuringPointId, hasFeedRules])
  const stopCheck = useMemo(() => {
    const own = latestReadingsOfDay(waterRows, today, timeZone)
    const point = latestReadingsOfDay(pointWaterRows, today, timeZone)
    const pick = (ownValue: typeof own.temperature, pointValue: typeof point.temperature) => (
      pickReading({ own: ownValue, point: pointValue, pointName: measuringPoint?.name, day: today, timeZone })
    )
    return checkFeedStop(feedRules, {
      temperature: pick(own.temperature, point.temperature),
      dissolvedOxygen: pick(own.dissolvedOxygen, point.dissolvedOxygen)
    })
  }, [feedRules, waterRows, pointWaterRows, measuringPoint?.name, today, timeZone])
  const fastingMoveDate = transferState?.kind === 'fasting' ? transferState.moveDate : null
  const [holdOverride, setHoldOverride] = useState(false)
  useEffect(() => {
    setHoldOverride(false)
  }, [selectedLotId, productionUnitId])

  // Findings #182: the plan's feeds (one, or two in a transition) and how the
  // office wants this unit recorded. A stale plan falls back to the free entry.
  const planEntries = useMemo(() => resolvePlanFeedItems(feedPlan, farmFeedItems), [feedPlan, farmFeedItems])
  const planItem = planEntries[0]?.item ?? null
  const planMode = feedPlanMode(feedPlan)
  const weighedMode = Boolean(feedPlan && planEntries.length > 0 && planMode === 'weighed')
  const planItemIdRef = useRef<string | null>(null)
  planItemIdRef.current = planItem?.id ?? null

  useEffect(() => {
    setSelectedItemId('')
    setFeedKgDraft('')
    if (!selectedLotId) {
      setExistingLogsForLot([])
      return
    }
    let cancelled = false
    void feedLogApi.list({ farmId, lotId: selectedLotId }, { preferLocal: true }).then((logs) => {
      if (cancelled) return
      setExistingLogsForLot(logs)
      // Office Plans v0 AC #2: the plan's feed wins over "last feed".
      if (planItemIdRef.current) {
        setSelectedItemId(planItemIdRef.current)
        return
      }
      // Field Bundle v0 §3 item 9: pre-select the last feed used in this
      // tank (never carried over from another tank).
      const lastFeedType = lastFeedTypeForTank(logs, { lotId: selectedLotId, productionUnitId })
      const match = lastFeedType ? feedItemsRef.current.find((item) => feedTypeForItem(item) === lastFeedType) : undefined
      if (match) {
        setSelectedItemId(match.id)
        requestAnimationFrame(() => feedKgInputRef.current?.focus())
      }
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmId, selectedLotId, productionUnitId])

  useEffect(() => {
    if (planItem && selectedLotId && !selectedItemId) setSelectedItemId(planItem.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planItem?.id, selectedLotId])

  const resetConfirmationState = () => {
    setValidationError(null)
    setLargeValuePending(false)
    setDuplicateWarning(null)
  }

  const handleSelectItem = (itemId: string) => {
    setSelectedItemId(itemId)
    setFeedKgDraft('')
    resetConfirmationState()
    requestAnimationFrame(() => feedKgInputRef.current?.focus())
  }

  const selectedItem = useMemo(
    () => feedItems.find((item) => item.id === selectedItemId) ?? null,
    [feedItems, selectedItemId]
  )

  const handleConfirm = async (override?: { item: InventoryItem; kgDraft: string }) => {
    const item = override?.item ?? selectedItem
    const kgDraft = override?.kgDraft ?? feedKgDraft
    if (!item || !farmId || !productionUnitId || !selectedLotId) return

    const validation = validateFeedKgValue(kgDraft)
    if (!validation.ok) {
      setValidationError(t('crewMode.feed.error.invalidValue'))
      return
    }
    setValidationError(null)

    const feedKgValue = Number(kgDraft.trim().replace(',', '.'))

    if (!largeValuePending && isUnusuallyLargeFeedKg(feedKgValue)) {
      setLargeValuePending(true)
      return
    }

    const recordedAtIso = new Date().toISOString()
    const duplicate = findLikelyDuplicateFeedLog({
      lotId: selectedLotId,
      recordedAtIso,
      existingLogs: existingLogsForLot
    })
    if (duplicate && duplicateWarning?.id !== duplicate.id) {
      setDuplicateWarning(duplicate)
      return
    }

    await createFeedings([{ item, kg: feedKgValue }], recordedAtIso)
    // Same tank, same diet: keep the item, clear only the quantity.
    setSelectedItemId(item.id)
    setFeedKgDraft('')
    resetConfirmationState()
  }

  /**
   * Saves one feeding per entry (a mix: one record per component) and shows
   * the last-entry status. Findings #182: the plan button can save two feeds
   * at once, and "Pesada del día" adds its weights to each record's metadata.
   */
  const createFeedings = async (
    entries: Array<{ item: InventoryItem; kg: number; metadataFor?: (kg: number) => Record<string, unknown> }>,
    recordedAtIso: string
  ): Promise<FeedLog[]> => {
    if (!farmId || !productionUnitId || !selectedLotId || entries.length === 0) return []
    const capturedByActorId = getCurrentOperatorActorId()
    const recordMetadata = recordMetadataRef.current
    const payloads = entries.flatMap(({ item, kg: feedKgValue, metadataFor }) => {
      const mix = readMix(item)
      // Office Plans v0 §3.2: a mix becomes one record per component, each
      // with its own feed type (own cost and stock) and delivery lot number.
      const built = mix
        ? buildMixFeedCapturePayloads({
          farmId,
          productionUnitId,
          lotId: selectedLotId,
          lot: location?.lotKey,
          mixId: createId('mix'),
          mixName: item.name,
          components: splitMixKg(feedKgValue, mix).map((part) => {
            const componentItem = feedItemsRef.current.find((candidate) => candidate.id === part.itemId)
            return {
              feedType: componentItem ? feedTypeForItem(componentItem) : part.itemId,
              percent: part.percent,
              kg: part.kg,
              feedLotNumber: latestReceiptLotNumber(componentItem)
            }
          }),
          recordedAtIso,
          capturedByActorId
        })
        : [buildFeedCaptureCreatePayload({
          farmId,
          productionUnitId,
          lotId: selectedLotId,
          lot: location?.lotKey,
          feedType: feedTypeForItem(item),
          feedKgValue,
          recordedAtIso,
          capturedByActorId,
          feedLotNumber: latestReceiptLotNumber(item)
        })]
      const withOwn = metadataFor
        ? built.map((payload) => ({ ...payload, metadata: { ...(payload.metadata ?? {}), ...metadataFor(payload.feedKg) } }))
        : built
      return recordMetadata
        ? withOwn.map((payload) => ({ ...payload, metadata: { ...(payload.metadata ?? {}), ...recordMetadata } }))
        : withOwn
    })

    const results: FeedLogMutationResult[] = []
    for (const payload of payloads) {
      results.push(await feedLogApi.create(payload, { deriveCostServerSide: true, source: 'crewMode' }))
    }
    if (results.length === 0) return []
    // Findings #136: like Mortalidad/Muestreo, make sure the feed's farm day has
    // its Base de datos row, or the feeding stays out of the day's feed kg.
    const entryDayKey = farmDayKey(recordedAtIso, farmTimeZone(farmId)) ?? undefined
    void ensureTodayProductionRow({ farmId, productionUnitId }, { id: selectedLotId, lotKey: location?.lotKey ?? '' }, entryDayKey).catch(() => {})
    const ids = results.map((result) => result.data.id)
    const firstId = ids[0]
    setExistingLogsForLot((prev) => [...prev, ...results.map((result) => result.data)])

    const initialState: SyncBadgeState = results.every((result) => result.syncState === 'synced')
      ? 'synced'
      : results.some((result) => result.syncState === 'pending') ? 'pending' : 'local'

    recordCrewEvent('capture.confirm', {
      stream: 'feed',
      farmId,
      syncState: initialState,
      capturedByActorId
    })

    setLastCapture({
      feedLogId: firstId,
      feedLogIds: ids,
      itemName: entries.map((entry) => entry.item.name).join(' + '),
      feedKgLabel: entries.map((entry) => formatKg(entry.kg, locale)).join(' + '),
      locationLabel: location?.label ?? '',
      state: initialState
    })

    Promise.all(results.map((result) => result.syncPromise ?? Promise.resolve())).then(async () => {
      const status = await feedLogApi.readFeedLogSyncStatus(ids)
      if (status.blockedCount > 0) {
        recordCrewEvent('capture.error', { stream: 'feed', farmId })
      }
      setLastCapture((prev) => (prev?.feedLogId === firstId
        ? { ...prev, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : 'pending') }
        : prev))
    }).catch(() => {
      recordCrewEvent('capture.error', { stream: 'feed', farmId })
      setLastCapture((prev) => (prev?.feedLogId === firstId ? { ...prev, state: 'error' } : prev))
    })
    return results.map((result) => result.data)
  }

  // Office Plans v0 §3.1 / AC #1: one tap records the planned ration.
  // Findings #182: every feed of the plan at once — kg/día ÷ tomas "Por toma",
  // the whole kg/día "Total del día".
  const planGive = feedPlan && planEntries.length > 0 && !weighedMode
    ? planEntries.map(({ item, planItem: entry }) => ({ item, kg: plannedKgFor(feedPlan, entry) }))
    : []
  const handleGivePlan = async () => {
    if (planGive.length === 0) return
    resetConfirmationState()
    const recordedAtIso = new Date().toISOString()
    const duplicate = findLikelyDuplicateFeedLog({ lotId: selectedLotId, recordedAtIso, existingLogs: existingLogsForLot })
    if (duplicate && duplicateWarning?.id !== duplicate.id) {
      setDuplicateWarning(duplicate)
      return
    }
    await createFeedings(planGive, recordedAtIso)
    setSelectedItemId(planGive[0].item.id)
    setFeedKgDraft('')
    setDuplicateWarning(null)
  }

  // Findings #182: "Pesada del día" — today's weighed records of this unit and lot.
  const todaysWeighings = existingLogsForLot.filter((log) => (
    log.productionUnitId === productionUnitId
    && Boolean(readDayWeighing(log))
    && farmDayKey(log.recordedAt, farmTimeZone(farmId)) === today
  ))
  const [weighedDrafts, setWeighedDrafts] = useState<Record<string, string>>({})
  const [leftoverDrafts, setLeftoverDrafts] = useState<Record<string, string>>({})
  const [weighingError, setWeighingError] = useState<string | null>(null)

  // Findings #210 / #211: nothing to feed while a hold applies, unless the supervisor
  // authorised it. A morning weighing already saved still takes its sobrante.
  const holdApplies = Boolean(selectedLotId) && (stopCheck.reasons.length > 0 || Boolean(fastingMoveDate))
  const holdActive = holdApplies && !holdOverride && !(weighedMode && todaysWeighings.length > 0)
  const recordMetadataRef = useRef<Record<string, unknown> | null>(null)
  recordMetadataRef.current = (() => {
    const out: Record<string, unknown> = {}
    if (holdApplies && holdOverride) {
      out.feedStop = {
        authorised: true,
        ...(stopCheck.reasons.length > 0 ? { reasons: stopCheck.reasons } : {}),
        ...(fastingMoveDate ? { fastingBeforeMove: fastingMoveDate } : {})
      }
    }
    if (backToNormalTapped && transferRation) Object.assign(out, backToNormalMetadata(transferRation.movedOn))
    return Object.keys(out).length > 0 ? out : null
  })()

  const handleBackToNormal = () => {
    if (!backToNormalKey) return
    saveJSON(backToNormalKey, true)
    setBackToNormalTapped(true)
  }

  useEffect(() => {
    setWeighedDrafts({})
    setLeftoverDrafts({})
    setWeighingError(null)
  }, [selectedLotId, productionUnitId])

  const handleSaveWeighed = async () => {
    const entries: Array<{ item: InventoryItem; kg: number; metadataFor: (kg: number) => Record<string, unknown> }> = []
    for (const { item } of planEntries) {
      const draft = (weighedDrafts[item.id] ?? '').trim()
      if (!draft) continue
      const validation = validateFeedKgValue(draft)
      if (!validation.ok) {
        setWeighingError(t('crewMode.feed.weighed.error.weighed'))
        return
      }
      entries.push({
        item,
        kg: Number(draft.replace(',', '.')),
        metadataFor: (kg) => withDayWeighing({ planItemId: item.id }, { weighedKg: kg, leftoverKg: null })
      })
    }
    if (entries.length === 0) {
      setWeighingError(t('crewMode.feed.weighed.error.weighed'))
      return
    }
    setWeighingError(null)
    await createFeedings(entries, new Date().toISOString())
    setWeighedDrafts({})
  }

  const handleSaveLeftover = async () => {
    const open = todaysWeighings.filter((log) => readDayWeighing(log)?.leftoverKg === null)
    const updates: Array<{ log: FeedLog; leftoverKg: number }> = []
    for (const log of open) {
      const weighing = readDayWeighing(log)
      if (!weighing) continue
      const parsed = parseLeftover(leftoverDrafts[log.id] ?? '', weighing.weighedKg)
      if (!parsed.ok) {
        setWeighingError(t('crewMode.feed.weighed.error.leftover'))
        return
      }
      updates.push({ log, leftoverKg: parsed.kg })
    }
    setWeighingError(null)
    const updated: FeedLog[] = []
    for (const { log, leftoverKg } of updates) {
      const weighing = { weighedKg: readDayWeighing(log)?.weighedKg ?? 0, leftoverKg }
      const result = await feedLogApi.update(log.id, {
        feedKg: fedFromWeighing(weighing),
        metadata: withDayWeighing(log.metadata, weighing)
      }, { deriveCostServerSide: true })
      updated.push(result.data)
    }
    setExistingLogsForLot((prev) => prev.map((log) => updated.find((entry) => entry.id === log.id) ?? log))
    setLeftoverDrafts({})
    setLastCapture(null)
    recordCrewEvent('capture.confirm', { stream: 'feed', farmId, syncState: 'local', capturedByActorId: getCurrentOperatorActorId() })
  }

  // Field Bundle v0 §3 item 6: undo the operator's own last entry (audited
  // delete, automatic reason, operator identity from the PIN session).
  const handleUndo = async () => {
    if (!lastCapture) return
    const ids = lastCapture.feedLogIds
    for (const id of ids) {
      await feedLogApi.delete(id, buildOperatorUndoAudit(t('crewCorrection.reason.operatorUndo')))
    }
    setExistingLogsForLot((prev) => prev.filter((log) => !ids.includes(log.id)))
    setLastCapture(null)
    recordCrewEvent('capture.undo', { stream: 'feed', farmId })
  }

  const handleRetry = async () => {
    if (!lastCapture) return
    await feedLogApi.retryFeedLogSyncStatusWithReport(lastCapture.feedLogIds)
    const status = await feedLogApi.readFeedLogSyncStatus(lastCapture.feedLogIds)
    setLastCapture((prev) => (prev?.feedLogId === lastCapture.feedLogId
      ? { ...prev, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : 'pending') }
      : prev))
  }

  // Findings #137: flip the badge once the background flush sends it.
  useRecheckWhileUnsynced(isUnsyncedBadgeState(lastCapture?.state), async () => {
    if (!lastCapture) return
    const firstId = lastCapture.feedLogId
    const status = await feedLogApi.readFeedLogSyncStatus(lastCapture.feedLogIds)
    setLastCapture((prev) => (prev?.feedLogId === firstId
      ? { ...prev, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : prev.state) }
      : prev))
  })

  if (farmId && !isFeedCrewModeEnabled(farmId)) {
    return (
      <div style={{ maxWidth: 420, margin: '0 auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <CrewModeFarmBar farmId={farmId} farms={farms} onSelectFarm={selectFarm} />
        <div data-testid="feed-capture-flag-disabled" style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>
          {t('crewMode.feed.notEnabledForFarm')}
        </div>
      </div>
    )
  }

  return (
    <OperatorGate>
      <div style={{ maxWidth: 420, margin: '0 auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <CrewModeFarmBar farmId={farmId} farms={farms} onSelectFarm={selectFarm} />

        <CrewLocationHeader stream="feed" location={location} onChange={crewLocation.openPicker} />
        <CrewLocationPicker
          stream="feed"
          open={crewLocation.pickerOpen}
          options={crewLocation.options}
          currentKey={location?.key}
          onChoose={(option) => {
            crewLocation.choose(option)
            setLastCapture(null)
          }}
          onClose={crewLocation.closePicker}
        />

        {holdActive && (
          <FeedHoldPanel
            reasons={stopCheck.reasons}
            fastingMoveDate={fastingMoveDate}
            timeZone={timeZone}
            onMeasure={() => navigate('/crew/water')}
            onOverride={() => setHoldOverride(true)}
          />
        )}

        {selectedLotId && !holdActive && stopCheck.reasons.length === 0 && stopCheck.missing.length > 0 && (
          <div data-testid="feed-capture-missing-reading" style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 10, backgroundColor: '#f3f4f6', color: '#374151', fontSize: 14 }}>
            <span>
              {t(measuringPoint ? 'crewMode.feed.hold.missingWithPoint' : 'crewMode.feed.hold.missing', {
                params: stopCheck.missing.map((parameter) => t(`crewMode.feed.hold.missing.${parameter}`)).join(' / '),
                unit: measuringPoint?.name ?? ''
              })}
            </span>
            <button
              type="button"
              onClick={() => navigate('/crew/water')}
              style={{ minHeight: 40, borderRadius: 10, border: '1px solid #1d4ed8', backgroundColor: 'white', color: '#1d4ed8', fontWeight: 700, padding: '4px 10px', whiteSpace: 'nowrap' }}
            >
              {t('crewMode.feed.hold.measure')}
            </button>
          </div>
        )}

        {selectedLotId && !holdActive && reducedState && (
          <div data-testid="feed-capture-reduced" style={{ display: 'grid', gap: 6, padding: 12, borderRadius: 12, border: '2px solid #b45309', backgroundColor: '#fffbeb', color: '#78350f' }}>
            <strong style={{ fontSize: 16 }}>{t('crewMode.feed.reduced.title', { day: reducedState.day, of: reducedState.of })}</strong>
            <span data-testid="feed-capture-reduced-kg" style={{ fontWeight: 700 }}>
              {reducedState.kgPerDay === null
                ? t('crewMode.feed.reduced.noKg')
                : officePlan
                  ? t('crewMode.feed.reduced.give', { kg: formatKg(reducedState.kgPerDay, locale), plan: formatKg(feedPlanTotalKg(officePlan), locale) })
                  : t('crewMode.feed.reduced.giveNoPlan', { kg: formatKg(reducedState.kgPerDay, locale) })}
            </span>
            <button
              type="button"
              data-testid="feed-capture-back-to-normal"
              onClick={handleBackToNormal}
              style={{ justifySelf: 'start', minHeight: 44, borderRadius: 10, border: '1px solid #78350f', backgroundColor: 'white', color: '#78350f', fontWeight: 700, padding: '4px 12px' }}
            >
              {t('crewMode.feed.reduced.backToNormal')}
            </button>
          </div>
        )}

        {selectedLotId && !holdActive && backToNormalTapped && transferRation?.productionUnitId === productionUnitId && !reducedState && daysBetween(transferRation.movedOn, today) + 1 <= transferRation.band.reducedDays && (
          <div data-testid="feed-capture-back-to-normal-done" style={{ fontSize: 13, color: '#065f46', fontWeight: 600 }}>
            {t('crewMode.feed.reduced.backToNormalDone')}
          </div>
        )}

        {selectedLotId && !holdActive && feedPlan && planGive.length > 0 && (
          <button
            type="button"
            data-testid="feed-capture-give-plan"
            onClick={() => void handleGivePlan()}
            style={{ ...primaryButtonStyle, minHeight: 72, fontSize: 18, backgroundColor: '#047857', borderColor: '#047857' }}
          >
            {planMode === 'dayTotal'
              ? t('crewMode.feed.giveDayTotal', { feeds: planGive.map((entry) => `${formatKg(entry.kg, locale)} kg ${entry.item.name}`).join(' + ') })
              : planGive.length === 1
                ? t('crewMode.feed.givePlan', { kg: formatKg(planGive[0].kg, locale), feed: planGive[0].item.name })
                : t('crewMode.feed.givePlan', { kg: planGive.map((entry) => formatKg(entry.kg, locale)).join(' + '), feed: planGive.map((entry) => entry.item.name).join(' + ') })}
            <span data-testid="feed-capture-give-plan-detail" style={{ display: 'block', fontSize: 14, fontWeight: 500, marginTop: 4 }}>
              {planMode === 'dayTotal'
                ? t('crewMode.feed.giveDayTotalDetail')
                : t('crewMode.feed.givePlanDetail', { feedings: feedPlan.feedingsPerDay, kgDay: formatKg(feedPlanTotalKg(feedPlan), locale) })}
            </span>
          </button>
        )}

        {selectedLotId && !holdActive && weighedMode && (
          <div data-testid="feed-capture-weighed" style={{ display: 'grid', gap: 10, padding: 12, border: '1px solid #d1d5db', borderRadius: 12 }}>
            <strong>{t('crewMode.feed.weighed.title')}</strong>
            {todaysWeighings.length === 0 ? (
              <>
                <span style={{ fontSize: 13, color: '#6b7280' }}>{t('crewMode.feed.weighed.morningHelp')}</span>
                {planEntries.map(({ item, planItem: entry }) => (
                  <label key={item.id} style={{ display: 'grid', gap: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>
                      {t('crewMode.feed.weighed.weighedLabel', { feed: item.name })} · {t('crewMode.feed.weighed.planHint', { kg: formatKg(entry.kgPerDay, locale) })}
                    </span>
                    <input
                      data-testid={`feed-capture-weighed-${item.id}`}
                      type="text"
                      inputMode="decimal"
                      value={weighedDrafts[item.id] ?? ''}
                      onChange={(event) => setWeighedDrafts((prev) => ({ ...prev, [item.id]: event.target.value }))}
                      style={{ width: '100%', minHeight: 56, padding: '8px 14px', borderRadius: 10, border: '1px solid #d1d5db', fontSize: 24, fontWeight: 700 }}
                    />
                  </label>
                ))}
                <button type="button" data-testid="feed-capture-weighed-save" onClick={() => void handleSaveWeighed()} style={primaryButtonStyle}>
                  {t('crewMode.feed.weighed.saveWeighed')}
                </button>
              </>
            ) : (
              <>
                {todaysWeighings.map((log) => {
                  const weighing = readDayWeighing(log)
                  if (!weighing) return null
                  return weighing.leftoverKg === null ? (
                    <label key={log.id} style={{ display: 'grid', gap: 4 }}>
                      <span style={{ fontWeight: 700 }}>{t('crewMode.feed.weighed.weighedToday', { kg: formatKg(weighing.weighedKg, locale), feed: log.feedType ?? '' })}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>{t('crewMode.feed.weighed.leftoverLabel', { feed: log.feedType ?? '' })}</span>
                      <input
                        data-testid={`feed-capture-leftover-${log.id}`}
                        type="text"
                        inputMode="decimal"
                        value={leftoverDrafts[log.id] ?? ''}
                        onChange={(event) => setLeftoverDrafts((prev) => ({ ...prev, [log.id]: event.target.value }))}
                        style={{ width: '100%', minHeight: 56, padding: '8px 14px', borderRadius: 10, border: '1px solid #d1d5db', fontSize: 24, fontWeight: 700 }}
                      />
                    </label>
                  ) : (
                    <div key={log.id} data-testid={`feed-capture-fed-${log.id}`} style={{ fontWeight: 700, color: '#065f46' }}>
                      {t('crewMode.feed.weighed.fedToday', {
                        fed: formatKg(fedFromWeighing(weighing), locale),
                        feed: log.feedType ?? '',
                        weighed: formatKg(weighing.weighedKg, locale),
                        leftover: formatKg(weighing.leftoverKg, locale)
                      })}
                    </div>
                  )
                })}
                {todaysWeighings.some((log) => readDayWeighing(log)?.leftoverKg === null) ? (
                  <>
                    <span style={{ fontSize: 13, color: '#6b7280' }}>{t('crewMode.feed.weighed.leftoverHelp')}</span>
                    <button type="button" data-testid="feed-capture-leftover-save" onClick={() => void handleSaveLeftover()} style={primaryButtonStyle}>
                      {t('crewMode.feed.weighed.saveLeftover')}
                    </button>
                  </>
                ) : null}
              </>
            )}
            {weighingError ? (
              <div data-testid="feed-capture-weighed-error" style={{ color: '#dc2626', fontWeight: 700 }}>{weighingError}</div>
            ) : null}
          </div>
        )}

        {selectedLotId && !holdActive && !weighedMode && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 8 }}>{t('crewMode.feed.selectItem')}</div>
            {feedItems.length === 0 ? (
              <div data-testid="feed-capture-no-items" style={{ color: '#6b7280' }}>{t('crewMode.feed.noItems')}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {feedItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`feed-capture-item-${item.id}`}
                    onClick={() => handleSelectItem(item.id)}
                    style={{
                      ...chipButtonStyle,
                      borderColor: selectedItemId === item.id ? '#1d4ed8' : '#d1d5db',
                      backgroundColor: selectedItemId === item.id ? '#eff6ff' : 'white'
                    }}
                  >
                    {item.name}
                    {readMix(item) && (
                      <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>{t('crewMode.feed.mixBadge')}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedItem && !holdActive && !weighedMode && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>
              {t('crewMode.feed.quantity')} ({t('crewMode.feed.quantityUnit')})
            </label>
            <input
              ref={feedKgInputRef}
              data-testid="feed-capture-quantity-input"
              type="text"
              inputMode="decimal"
              value={feedKgDraft}
              enterKeyHint="done"
              onChange={(event) => {
                setFeedKgDraft(event.target.value)
                resetConfirmationState()
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void handleConfirm()
                }
              }}
              style={{
                width: '100%',
                minHeight: 56,
                padding: '8px 14px',
                borderRadius: 10,
                border: validationError ? '2px solid #dc2626' : '1px solid #d1d5db',
                fontSize: 24,
                fontWeight: 700
              }}
            />
            {validationError && (
              <div data-testid="feed-capture-validation-error" style={{ marginTop: 6, color: '#dc2626', fontWeight: 700 }}>
                {validationError}
              </div>
            )}
            {largeValuePending && (
              <div data-testid="feed-capture-large-value-warning" style={{ marginTop: 6, color: '#92400e', fontWeight: 700 }}>
                {t('crewMode.feed.warning.largeValue')}
              </div>
            )}
            {duplicateWarning && (
              <div data-testid="feed-capture-duplicate-warning" style={{ marginTop: 6, color: '#92400e', fontWeight: 700 }}>
                {t('crewMode.feed.warning.duplicate')}
              </div>
            )}
          </div>
        )}

        {selectedItem && !holdActive && !weighedMode && (
          <button
            type="button"
            data-testid="feed-capture-confirm"
            onClick={() => void handleConfirm()}
            style={primaryButtonStyle}
          >
            {largeValuePending || duplicateWarning ? t('crewMode.feed.confirmAnyway') : t('crewMode.feed.confirm')}
          </button>
        )}

        {lastCapture && (
          <div
            data-testid="feed-capture-last-entry"
            style={{
              padding: 12,
              borderRadius: 10,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              ...syncBadgeStyle[lastCapture.state]
            }}
          >
            <span style={{ fontWeight: 700 }}>
              <span data-testid="feed-capture-last-location" style={{ display: 'block', fontSize: 13 }}>{lastCapture.locationLabel}</span>
              {lastCapture.itemName}: {lastCapture.feedKgLabel} {t('crewMode.feed.quantityUnit')}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                data-testid="feed-capture-undo"
                onClick={() => void handleUndo()}
                style={{ ...chipButtonStyle, minHeight: 44, minWidth: 44, fontSize: 13, padding: '4px 10px', width: 'auto' }}
              >
                {t('crewMode.undo')}
              </button>
              <span data-testid="feed-capture-sync-state" style={{ fontWeight: 800 }}>
                {t(syncBadgeLabelKey[lastCapture.state])}
              </span>
              {lastCapture.state === 'error' && (
                <button
                  type="button"
                  data-testid="feed-capture-retry"
                  onClick={() => void handleRetry()}
                  style={{ ...chipButtonStyle, minHeight: 40, minWidth: 40, fontSize: 12, padding: '4px 10px', width: 'auto' }}
                >
                  {t('crewMode.feed.retry')}
                </button>
              )}
            </span>
          </div>
        )}

        {lastCapture && (
          <CrewNextLocationButton
            next={crewLocation.next}
            onGo={(option) => {
              crewLocation.choose(option)
              setLastCapture(null)
            }}
          />
        )}
      </div>
    </OperatorGate>
  )
}
